import { useEffect, useRef, useState } from 'react'
import {
  AppDialog,
  Button,
  ConfirmActionModal,
  MiniLoader,
  StatusPill,
  Textarea,
} from '../Common'
import { useNls } from '../../nls/nlsProvider'
import {
  getEduPtmPrivateNotes,
  setEduPtmPrivateNotes,
  setEduPtmSlotOutcome,
  publishEduPtmSummary,
  mapEduPtmError,
  type EduPtmBookingStatus,
} from '../../services/eduPtmService'
import './eduPtm.css'

export interface EduPtmOutcomeNotesDialogProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  bookingId: string
  learnerName: string
  currentStatus: EduPtmBookingStatus
  onUpdated: () => void
}

export const EduPtmOutcomeNotesDialog = ({
  isOpen,
  onClose,
  workspaceId,
  bookingId,
  learnerName,
  currentStatus,
  onUpdated,
}: EduPtmOutcomeNotesDialogProps) => {
  const { t } = useNls()
  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  }, [t])

  const [status, setStatus] = useState<EduPtmBookingStatus>(currentStatus)
  const [notes, setNotes] = useState<string>('')
  const [summary, setSummary] = useState<string>('')

  const [isLoadingNotes, setIsLoadingNotes] = useState(false)
  const [isSavingNotes, setIsSavingNotes] = useState(false)
  const [isSavingOutcome, setIsSavingOutcome] = useState(false)
  const [isPublishingSummary, setIsPublishingSummary] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [confirmModalError, setConfirmModalError] = useState<string | null>(null)

  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [notesForbidden, setNotesForbidden] = useState(false)

  useEffect(() => {
    if (!isOpen || !bookingId) return

    let isMounted = true
    const loadNotes = async () => {
      try {
        setIsLoadingNotes(true)
        setErrorMsg(null)
        setNotesForbidden(false)
        const res = await getEduPtmPrivateNotes({ workspaceId, bookingId })
        if (isMounted) {
          setNotes(res.notes || '')
        }
      } catch (err: unknown) {
        if (isMounted) {
          const errKey = mapEduPtmError(err)
          if (errKey === 'NLSEduPtmErrorNoteStaffOnly' || errKey === 'NLSEduPtmErrorPermissionDenied') {
            setNotesForbidden(true)
          } else {
            setErrorMsg(tRef.current(errKey))
          }
        }
      } finally {
        if (isMounted) setIsLoadingNotes(false)
      }
    }

    void loadNotes()
    return () => {
      isMounted = false
    }
  }, [isOpen, workspaceId, bookingId])

  const handleOutcome = async (outcome: 'MET' | 'NO_SHOW') => {
    try {
      setIsSavingOutcome(true)
      setErrorMsg(null)
      await setEduPtmSlotOutcome({ workspaceId, bookingId, outcome })
      setStatus(outcome)
      onUpdated()
    } catch (err: unknown) {
      setErrorMsg(tRef.current(mapEduPtmError(err)))
    } finally {
      setIsSavingOutcome(false)
    }
  }

  const handleSaveNotes = async () => {
    try {
      setIsSavingNotes(true)
      setErrorMsg(null)
      await setEduPtmPrivateNotes({ workspaceId, bookingId, notes })
      onUpdated()
    } catch (err: unknown) {
      setErrorMsg(tRef.current(mapEduPtmError(err)))
    } finally {
      setIsSavingNotes(false)
    }
  }

  const handlePublishSummary = async () => {
    try {
      setIsPublishingSummary(true)
      setConfirmModalError(null)
      await publishEduPtmSummary({
        workspaceId,
        bookingId,
        summary: summary.trim(),
      })
      setIsConfirmModalOpen(false)
      onUpdated()
      onClose()
    } catch (err: unknown) {
      setConfirmModalError(tRef.current(mapEduPtmError(err)))
    } finally {
      setIsPublishingSummary(false)
    }
  }

  return (
    <>
      <AppDialog
        open={isOpen && !isConfirmModalOpen}
        onDismiss={onClose}
        title={t('NLSEduPtmOutcomeNotesTitle')}
        size="md"
        actions={[
          {
            label: t('NLSCommonActionClose'),
            onClick: onClose,
            variant: 'secondary',
          },
        ]}
      >
        <div className="eduPtmEditor">
          {errorMsg && (
            <div className="eduDialog__banner eduDialog__banner--error" role="alert">
              {errorMsg}
            </div>
          )}

          <div className="eduPtmCard__top">
            <span className="eduPtmCard__title">{learnerName}</span>
            <StatusPill
              tone={
                status === 'MET'
                  ? 'success'
                  : status === 'NO_SHOW'
                    ? 'danger'
                    : 'info'
              }
            >
              {status === 'MET'
                ? t('NLSEduPtmSlotStatusMet')
                : status === 'NO_SHOW'
                  ? t('NLSEduPtmSlotStatusNoShow')
                  : t('NLSEduPtmSlotStatusBooked')}
            </StatusPill>
          </div>

          {/* Outcome Section */}
          <div className="eduPtmEditor__section">
            <span className="eduPtmEditor__label">{t('NLSEduPtmOutcomeNotesTitle')}</span>
            <div className="eduPtmSlotCard__actions">
              <Button
                variant={status === 'MET' ? 'primary' : 'secondary'}
                onClick={() => handleOutcome('MET')}
                disabled={isSavingOutcome}
              >
                {t('NLSEduPtmMarkMetButton')}
              </Button>
              <Button
                variant={status === 'NO_SHOW' ? 'danger' : 'secondary'}
                onClick={() => handleOutcome('NO_SHOW')}
                disabled={isSavingOutcome}
              >
                {t('NLSEduPtmMarkNoShowButton')}
              </Button>
            </div>
          </div>

          {/* Private Notes Section */}
          <div className="eduPtmEditor__section">
            <div className="eduPtmSection__header">
              <span className="eduPtmEditor__label">{t('NLSEduPtmPrivateNotesTitle')}</span>
              <span className="eduPtmStaffOnlyBadge">
                {t('NLSEduPtmPrivateNotesStaffBadge')}
              </span>
            </div>

            {isLoadingNotes ? (
              <MiniLoader />
            ) : notesForbidden ? (
              <p className="eduPtmEditor__help">{t('NLSEduPtmErrorNoteStaffOnly')}</p>
            ) : (
              <>
                <Textarea
                  value={notes}
                  onChange={(val) => setNotes(val)}
                  placeholder={t('NLSEduPtmPrivateNotesPlaceholder')}
                  aria-label={t('NLSEduPtmPrivateNotesTitle')}
                  rows={3}
                  disabled={isSavingNotes}
                />
                <Button
                  variant="secondary"
                  onClick={handleSaveNotes}
                  disabled={isSavingNotes}
                >
                  {t('NLSEduPtmSaveNotesButton')}
                </Button>
              </>
            )}
          </div>

          {/* Summary for Family Section */}
          <div className="eduPtmEditor__section">
            <span className="eduPtmEditor__label">{t('NLSEduPtmSummaryTitle')}</span>
            <Textarea
              value={summary}
              onChange={(val) => setSummary(val)}
              placeholder={t('NLSEduPtmSummaryPlaceholder')}
              aria-label={t('NLSEduPtmSummaryTitle')}
              rows={3}
              disabled={isPublishingSummary}
            />
            <Button
              variant="primary"
              onClick={() => {
                setIsConfirmModalOpen(true)
                setConfirmModalError(null)
              }}
              disabled={isPublishingSummary || !summary.trim()}
            >
              {t('NLSEduPtmPublishSummaryButton')}
            </Button>
          </div>
        </div>
      </AppDialog>

      {/* Confirmation Modal before publishing summary to family */}
      {isConfirmModalOpen && (
        <ConfirmActionModal
          open={isConfirmModalOpen}
          onClose={() => {
            setIsConfirmModalOpen(false)
            setConfirmModalError(null)
          }}
          onConfirm={handlePublishSummary}
          title={t('NLSEduPtmPublishSummaryConfirmTitle')}
          body={t('NLSEduPtmPublishSummaryConfirmBody')}
          confirmLabel={t('NLSEduPtmPublishSummaryButton')}
          cancelLabel={t('NLSCommonActionCancel')}
          busy={isPublishingSummary}
        >
          {confirmModalError && (
            <div className="eduDialog__banner eduDialog__banner--error" role="alert" style={{ marginTop: 12 }}>
              {confirmModalError}
            </div>
          )}
        </ConfirmActionModal>
      )}
    </>
  )
}
